import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Linkedin } from "lucide-react";

const Footer = () => {
  const location = useLocation();

  const handleNavClick = (href, e) => {
    if (href === "/") {
      e.preventDefault();
      // If we're not on the home page, navigate there first
      if (location.pathname !== "/") {
        window.location.href = "/";
        return;
      }
      // If we're on the home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.pushState(null, null, "/");
    } else if (href.startsWith("/#")) {
      e.preventDefault();
      const sectionId = href.substring(2); // Remove "/#"

      // If we're not on the home page, navigate there first
      if (location.pathname !== "/") {
        window.location.href = href;
        return;
      }

      // If we're on the home page, scroll to the section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        // Update the URL hash
        window.history.pushState(null, null, href);
      }
    }
  };
  const footerLinks = {
    company: [
      { name: "Home", href: "/" },
      { name: "Challenges", href: "/#challenges" },
      { name: "Solutions", href: "/#solutions" },
      { name: "Why Us", href: "/#why-us" }
    ],
    resources: [
      { name: "Blog", href: "/#blog" },
      { name: "Beta Program", href: "/#beta-program" },
      { name: "Contact Us", href: "/#contact" }
    ],
    legal: [
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" }
    ]
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-slate-700 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <span className="text-2xl font-bold">MyFinBrain</span>
            </div>
            <p className="text-slate-300 mb-6 max-w-md">
              AI-Powered Fundamental Intelligence. Transforming unstructured financial documents into actionable insights with unparalleled explainability and auditability.
            </p>
            <div className="flex space-x-4">
              <a
                href="https://www.linkedin.com/in/vivekgavri/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-white transition-colors duration-200"
              >
                <Linkedin size={24} />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={(e) => handleNavClick(link.href, e)}
                    className="text-slate-300 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={(e) => handleNavClick(link.href, e)}
                    className="text-slate-300 hover:text-white transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
            <p className="text-slate-300">
              © 2025 MyFinBrain. All Rights Reserved.
            </p>
            <div className="flex space-x-4">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={(e) => handleNavClick(link.href, e)}
                  className="text-slate-300 hover:text-white transition-colors duration-200 text-sm"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="mt-4 md:mt-0">
            <p className="text-slate-400 text-sm">
              Contact: <a href="mailto:hello@myfinbrain.com" className="text-slate-300 hover:text-white">hello@myfinbrain.com</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;